<template>
    <div class="entry-teaser" :class="rootClasses">
        <conditional-link class="u-reset entry-teaser--link" :to="routerLinkParams">
            <div class="entry-teaser--content">
                <h3 class="u-reset entry-teaser--title">
                    {{ shortenedTitle }}
                </h3>
                <ul class="u-reset entry-teaser--bullets">
                    <li class="entry-teaser--bullet"
                            :class="bulletClass"
                            v-for="bulletClass in bulletClasses"
                            :key="bulletClass">
                        <span class="entry-teaser--bullet-icon"/>
                    </li>
                </ul>
            </div>
            <div class="entry-teaser--arrow">
                <ui-icon symbol="chevron-right" size="small"/>
            </div>
            <transition name="trs-delayed-fade-enter">
                <div class="entry-teaser--close" v-if="isActiveEntry">
                    <button class="entry-teaser--close-btn" @click.prevent="onClose">
                        <ui-icon symbol="close" size="medium"/>
                    </button>
                </div>
            </transition>
        </conditional-link>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { MAP_APP_RECORD_NAMES } from 'framework/router/route-data-configs';
    import { bemMixin } from '@dreipol/vue-ui/src/mixins';
    import ConditionalLink from 'framework/components/conditional-link/conditional-link';
    import UiIcon from '@dreipol/vue-ui/src/components/icon/icon.vue';

    /**
     * Reduce text to a word boundary before the given threshold
     * @param {string} text - The target text
     * @param {number} threshold - The given threshold
     * @return {string} The shortened text
     */
    function shortenText(text, threshold) {
        if (isNaN(threshold) || text.length <= threshold) {
            return text;
        }

        const clippedText = text.substr(0, threshold - 1);
        const shortenedTitle = clippedText.substr(0, Math.min(clippedText.length, clippedText.lastIndexOf(' ')));

        return `${ shortenedTitle }…`;
    }

    export default {
        components: {
            ConditionalLink,
            UiIcon,
        },
        mixins: [
            bemMixin('entry-teaser'),
        ],
        inheritAttrs: false,
        props: {
            id: {
                type: String,
                required: true,
            },
            slug: {
                type: String,
                required: true,
            },
            parentMainCategorySlug: {
                type: String,
                default: null,
            },
            parentSubCategorySlug: {
                type: String,
                default: null,
            },
            title: {
                type: String,
                required: true,
            },
            titleMaxLength: {
                type: Number,
                default() {
                    return null;
                },
            },
            /** @type {string[]} */
            mainCategoryIds: {
                type: Array,
                required: true,
            },
        },
        computed: {
            ...mapGetters('project-data', ['activeEntry', 'activeMainCategory', 'mainCategories']),
            isActiveEntry() {
                return !!this.activeEntry && this.activeEntry.id === this.id;
            },
            rootClasses() {
                return [
                    ...this.bemFacets,
                    this.bemAdd(this.isActiveEntry ? 'is-active-entry' : ''),
                ];
            },
            routerLinkParams() {
                const { ENTRY_ONLY, SUB_CATEGORY_ENTRY } = MAP_APP_RECORD_NAMES;
                const name = this.activeMainCategory ? SUB_CATEGORY_ENTRY : ENTRY_ONLY;
                const params = {
                    maincategory: this.parentMainCategorySlug,
                    subcategory: this.parentSubCategorySlug,
                    entry: this.slug,
                };

                return { name, params };
            },
            shortenedTitle() {
                return shortenText(this.title, this.titleMaxLength);
            },
            bulletClasses() {
                return this.mainCategories
                    .filter(({ id }) => this.mainCategoryIds.includes(id))
                    .map(({ color }) => this.bemAdd(`main-category-facet-${ color }`, 'bullet'));
            },
        },
        methods: {
            onClose() {
                const { MAIN_CATEGORY_ONLY, MAP } = MAP_APP_RECORD_NAMES;
                const { maincategory } = this.$route.params;

                this.$router.push({
                    name: maincategory ? MAIN_CATEGORY_ONLY : MAP,
                    params: {
                        maincategory: maincategory,
                    },
                });
            },
        },
    };
</script>
